import { ReactNode, SyntheticEvent, useState } from "react";
import { Box, Button, Container, Tab, Tabs } from "@mui/material";
import { Typography } from "@appsflyer/fe-ui-core";
import { Trans, useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import { DJSimpleCodeBlock } from "../components/codeblock";
import transition from "../components/transition";
import { ISessionProps, OSType, SessionStatus } from "../components/IProgress";
import { amplitudeTrack_continue } from "../../services/utils";

const IntegrateSDKWithAI = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const appId = location.state?.appID;
  const lsDevKey = localStorage.getItem("devKey");
  const devKey = lsDevKey ? JSON.parse(lsDevKey) : "";
  const cursorMcpJson = `"appsflyer-sdk-mcp": {
  "command": "npx",
  "args": [
    "-y",
    "@appsflyer/sdk-mcp-server"
  ],
  "env": {
    "APP_ID": "${appId}",
    "DEV_KEY": "${devKey}"
  }
}`;
  const cursorMcpInstallUrl = `cursor://anysphere.cursor-deeplink/mcp/install?name=appsFlyer-sdk-mcp&config=${encodeURIComponent(
    btoa(
      JSON.stringify({
        command: "npx",
        args: ["-y", "@appsflyer/sdk-mcp-server"],
        env: {
          APP_ID: appId ?? "",
          DEV_KEY: devKey ?? "",
        },
      })
    )
  )}`;
  const claudeMcpCommand = `claude mcp add --transport stdio appsFlyer-sdk-mcp \\
  -e APP_ID=${appId ?? ""} \\
  -e DEV_KEY=${devKey ?? ""} \\
  -- npx -y @appsflyer/sdk-mcp-server`;

  const isIOS = location.state?.os === OSType.iOS;
  const isAndroid = location.state?.os === OSType.Android;

  if (!isAndroid && !isIOS) {
    return <Navigate to="/notfound" replace />;
  }

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  function finishProcess() {
    const progressState: ISessionProps = {
      ...location.state,
      currentPage: location.state?.currentPage + 1,
      sessionStatus: SessionStatus.Success,
      completionStatus: true,
    };

    amplitudeTrack_continue(location.state);
    navigate("/finish", { state: progressState });
  }

  function tabIndexProps(idx: number) {
    return {
      index: idx,
      id: `tab-${idx}`,
      "aria-controls": `tab-panel-${idx}`,
    };
  }

  const handleChangeTab = (_event: SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setActiveStep(0);
  };

  const toolInstructions: { label: string; description: ReactNode }[] = [
    {
      label: t("integrate_android_sdk_with_ai_tab2_title"),
      description:
        (
          <>
            <Box sx={{ mb: 2 }}>

              <a href={cursorMcpInstallUrl}>
                <img
                  src="https://cursor.com/deeplink/mcp-install-dark.svg"
                  alt="Install MCP Server"
                  style={{
                    display: "block",
                    width: "150px",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: "8px",
                  }}
                />
              </a>
              <Typography sx={{ mt: 1.5, fontWeight: 600, fontSize: "0.95rem" }}>
                {t("integrate_android_sdk_with_ai_tab2_manual_instructions")}
              </Typography>
            </Box>
            <Box component="ol" sx={{ pl: 3, mb: 0 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="integrate_android_sdk_with_ai_tab2_description_1"
                  components={{ b: <b /> }}
                  />
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="integrate_android_sdk_with_ai_tab2_description_2"
                  components={{ b: <b /> }}
                  />
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="integrate_android_sdk_with_ai_tab2_description_3"
                  components={{ code: <code /> }}
                  />
              </Typography>
              <DJSimpleCodeBlock
                  codePrimary={cursorMcpJson}
                  showLineNumbers={false}
                  language="json"
                />
              <Typography component="li" sx={{ mb: 1 }}>
                {t("integrate_android_sdk_with_ai_tab2_description_4")}
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                <Trans
                  i18nKey="integrate_android_sdk_with_ai_tab2_description_1"
                  components={{ b: <b /> }}
                  />
              </Typography>
              <Typography component="li" sx={{ mb: 1 }}>
                {t("integrate_android_sdk_with_ai_tab2_description_6")}
              </Typography>
              <Typography component="li">
                <Trans
                  i18nKey="integrate_android_sdk_with_ai_tab2_description_7"
                  components={{ b: <b /> }}
                  />
              </Typography>
            </Box>
          </>
        ),
    },
    {
      label: t("integrate_android_sdk_with_ai_tab4_title"),
      description:
        (
          <Box component="ol" sx={{ pl: 3, mb: 0 }}>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab4_description_1"
                components={{ b: <b /> }}
              />
            </Typography>
            <DJSimpleCodeBlock
              codePrimary={claudeMcpCommand}
              showLineNumbers={false}
              language="bash"
            />
            <Typography component="li" sx={{ mb: 1 }}>
              {t("integrate_android_sdk_with_ai_tab4_description_2")}
            </Typography>
            <Typography component="li">
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab4_description_3"
                components={{ b: <b /> }}
              />
            </Typography>
          </Box>
        ),
    },
    {
      label: t("integrate_android_sdk_with_ai_tab1_title"),
      description:
        (
          <Box component="ol" sx={{ pl: 3, mb: 0 }}>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_1"
                components={{ b: <b /> }}
              />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_2"
                components={{ b: <b /> }}
              />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_3"
                components={{ b: <b /> }}
              />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_4"
                components={{ b: <b />, code: <code /> }}
              />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_5"
                components={{ b: <b />, code: <code /> }}
              />
              <Box component="ul" sx={{ mt: 1, pl: 3 }}>
                <Typography component="li" sx={{ mb: 1 }}>
                  <Trans
                    i18nKey="integrate_android_sdk_with_ai_tab1_description_5_1"
                    components={{ code: <code /> }}
                  />
                </Typography>
                <Typography component="li">
                  <Trans
                    i18nKey="integrate_android_sdk_with_ai_tab1_description_5_2"
                    components={{ code: <code /> }}
                  />
                </Typography>
              </Box>
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
                <Trans
                    i18nKey="integrate_android_sdk_with_ai_tab1_description_6"
                    components={{ b: <b />, code: <code /> }}
                  />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_7"
                components={{ b: <b /> }}
              />
            </Typography>
            <Typography component="li">
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab1_description_8"
                components={{ b: <b /> }}
                />
            </Typography>
          </Box>
        ),
    },
    ...(!isIOS ? [{
      label: t("integrate_android_sdk_with_ai_tab3_title"),
      description:
        (
          <Box component="ol" sx={{ pl: 3, mb: 0 }}>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_1"
                components={{ b: <b /> }}
                />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_2"
                components={{ b: <b /> }}
                />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_3"
                components={{ code: <code /> }}
                />
            </Typography>
            <DJSimpleCodeBlock
                codePrimary={cursorMcpJson}
                showLineNumbers={false}
                language="json"
              />
            <Typography component="li" sx={{ mb: 1 }}>
              {t("integrate_android_sdk_with_ai_tab3_description_4")}
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              {t("integrate_android_sdk_with_ai_tab3_description_5_1")}{" "}
              <Box
                component="img"
                src="https://files.readme.io/9b894552a614aedf25acae24a90f00d8594175f1802a757b43ffe9c835bdc810-Brand-Github-Copilot--Streamline-Tabler.png"
                alt="GitHub Copilot icon"
                sx={{
                  width: 24,
                  height: 24,
                  display: "inline-block",
                  verticalAlign: "text-bottom",
                  mx: 0.5,
                }}
              />
              {" "}<Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_5_2"
                components={{ b: <b /> }}
                />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_6"
                components={{ b: <b /> }}
                />
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              {t("integrate_android_sdk_with_ai_tab3_description_7_1")}{" "}
              <Box
                component="img"
                src="https://files.readme.io/5837294cf5b3b283cbd10f76085b81ec9598d3a705b71f5b8948e1a028ab972a-Tools--Streamline-Iconoir.png"
                alt="MCP Tools button"
                sx={{
                  width: 24,
                  height: 24,
                  display: "inline-block",
                  verticalAlign: "text-bottom",
                  mx: 0.5,
                }}
              />
              {" "}{t("integrate_android_sdk_with_ai_tab3_description_7_2")}
            </Typography>
            <Typography component="li" sx={{ mb: 1 }}>
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_8"
                components={{ b: <b /> }}
                />
            </Typography>
            <Typography component="li">
              {t("integrate_android_sdk_with_ai_tab3_description_9_1")}{" "}
              <Box
                component="img"
                src="https://files.readme.io/5837294cf5b3b283cbd10f76085b81ec9598d3a705b71f5b8948e1a028ab972a-Tools--Streamline-Iconoir.png"
                alt="MCP Log button"
                sx={{
                  width: 24,
                  height: 24,
                  display: "inline-block",
                  verticalAlign: "text-bottom",
                  mx: 0.5,
                }}
              />
              {" "}
              <Trans
                i18nKey="integrate_android_sdk_with_ai_tab3_description_9_2"
                components={{ b: <b /> }}
                />
               <br/>
              {t("integrate_android_sdk_with_ai_tab3_description_9_3")}
            </Typography>
          </Box>
        ),
    }] : []),
  ];

  const steps = [
    {
      label: t("integrate_android_sdk_with_ai_step1_title"),
      component: (
        <>
          <Typography sx={{ marginBottom: 2 }}>
            {t("integrate_android_sdk_with_ai_step1_description")}
          </Typography>
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            aria-label={isIOS ? "iOS AI coding tools" : "Android AI coding tools"}
          >
            {toolInstructions.map((tool, idx) => (
              <Tab key={tool.label} label={tool.label} {...tabIndexProps(idx)} />
            ))}
          </Tabs>
          <Box sx={{ marginTop: 2 }}>
            {typeof toolInstructions[tabValue].description === "string" ? (
              <Typography>{toolInstructions[tabValue].description}</Typography>
            ) : (
              toolInstructions[tabValue].description
            )}
          </Box>
        </>
      ),
    },
    {
      label: t("integrate_android_sdk_with_ai_step2_title"),
      component: (
        <Box component="ol" sx={{ pl: 3, mb: 0 }}>
          <Typography component="li" sx={{ mb: 1 }}>
            <Trans
              i18nKey="integrate_android_sdk_with_ai_step2_description_1"
              components={{ b: <b /> }} 
              />
          </Typography>
          <Typography component="li">
            {t("integrate_android_sdk_with_ai_step2_description_2_1")}
            <br/>
            {t("integrate_android_sdk_with_ai_step2_description_2_2")}
          </Typography>
        </Box>
      ),
    },
    {
      label: t("integrate_android_sdk_with_ai_step3_title"),
      component: (
        <Box component="ol" sx={{ pl: 3, mb: 0 }}>
          <Typography component="li" sx={{ mb: 1 }}>
            <Trans
              i18nKey={isIOS ? "integrate_ios_sdk_with_ai_step3_description_1" : "integrate_android_sdk_with_ai_step3_description_1"}
              components={{ b: <b /> }} 
              />
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            {t("integrate_android_sdk_with_ai_step3_description_2")}
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            {t("integrate_android_sdk_with_ai_step3_description_3")}
          </Typography>
          <Typography component="li" sx={{ mb: 1 }}>
            {t("integrate_android_sdk_with_ai_step3_description_4")}
            <Box component="ul" sx={{ mt: 1, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}>
                {t("integrate_android_sdk_with_ai_step3_description_4_1")}
              </Typography>
              <Typography component="li">
                {t("integrate_android_sdk_with_ai_step3_description_4_2")}
              </Typography>
            </Box>
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth={"lg"}>
      <Box padding={3} style={{ minHeight: "50vh" }}>
        <Typography variant="h1">
          {t(isIOS ? "integrate_ios_sdk_with_ai_title" : "integrate_android_sdk_with_ai_title")}
        </Typography>
        <Stepper
          activeStep={activeStep}
          style={{ border: "none" }}
          orientation="vertical"
        >
          {steps.map(({ label, component }, idx) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {component}
                <Box sx={{ mt: 2 }}>
                  {idx !== 0 && (
                    <Button variant="text" color="primary" onClick={handleBack}>
                      {t("general_Back")}
                    </Button>
                  )}
                  {idx !== steps.length - 1 && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleNext}
                    >
                      {t("general_Next")}
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Box>

      <Box
        display={"flex"}
        width={"100%"}
        justifyContent="space-between"
        alignSelf="end"
        justifySelf="end"
        marginTop={10}
        textAlign="end"
      >
        <Button
          variant="outlined"
          size="medium"
          color="secondary"
          style={{ marginRight: "5px" }}
          onClick={() => navigate(-1)}
        >
          {t("general_Back")}
        </Button>
        <Button
          variant="contained"
          size="medium"
          color="primary"
          onClick={() => finishProcess()}
          disabled={activeStep !== steps.length - 1}
        >
          {t("ip_InAppResults_android_doneButton")}
        </Button>
      </Box>
    </Container>
  );
};

export default transition(IntegrateSDKWithAI);
